import React, { useEffect, useState, useRef, useCallback, useContext, memo } from 'react';
import { useHistory, Link } from "react-router-dom";
import { useAtom } from 'jotai';
import axios from 'axios';
import config from '../Config';
import { useDebounce } from 'use-debounce';
import Modal from '../Components/Modal';
import CardModel from '../Components/CardModel';
import FindBar from '../Components/FindBar';
import Container from '../Layouts/Container';
import Header from '../Layouts/Header';
import Loading from '../Components/Loading';
import ScrollToTopButton from '../Components/ScrollToTopButton';
import DarkModeContext from '../Components/DarkModeContext';
import { authUserAtom, dataUserAtom, visionUserModeAtom } from '../Services/store/atoms';

const Landing = memo((props) => {
  const history = useHistory();
  const modalRef = useRef(null);
  const [categories, setCategories] = useState('');

  const [search, setSearch] = useState('');
  const [gender, setGender] = useState('All'); // Default to 'All' to show all genders
  const [models, setModels] = useState([]);
  const [value] = useDebounce(search, 1000);
  const [isLoading, setIsLoading] = useState(false);
  const [authUser] = useAtom(authUserAtom);
  const [dataUser] = useAtom(dataUserAtom);
  const [visionUserMode] = useAtom(visionUserModeAtom);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const { darkMode } = useContext(DarkModeContext);
  const observer = useRef();
  const fetchAbortRef = useRef(null);

  const lastModelRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && nextPageUrl) {
          fetchProducts(nextPageUrl, true);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, nextPageUrl]
  );

  const fetchProducts = useCallback((pageUrl = `${config.REACT_APP_BASE_URL}/api/v1/packs/listModel`, append = false) => {
    if (!append) {
      if (fetchAbortRef.current) fetchAbortRef.current.abort();
      fetchAbortRef.current = new AbortController();
    }

    setIsLoading(true);

    const requestData = new URLSearchParams();
    
    // Add search if exists
    if (value) requestData.append('search', value);
    
    // Add categories if exists and not 'All Categories'
    const categoriesValue = categories === 'All Categories' ? "" : categories;
    if (categoriesValue) requestData.append('categories', categoriesValue);
    
    // Add gender if not 'All'
    if (gender && gender !== 'All') {
      requestData.append('gender', gender.toLowerCase());
    }

    // Admin in vision-user mode: tell backend to apply country restrictions
    if (dataUser?.role === 'admin' && visionUserMode) {
      requestData.append('view_as_user', '1');
    }
    
    // Set headers
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      'Authorization': `Bearer ${authUser?.token}`
    };
    
    return axios.post(pageUrl, requestData.toString(), {
      headers,
      signal: !append ? fetchAbortRef.current.signal : undefined,
    })
      .then((response) => {
        setModels(prevModels => append ? [...prevModels, ...response.data.data.filter(model => !prevModels.some(prevModel => prevModel.id === model.id))] : response.data.data);
        setNextPageUrl(response.data.next_page_url);
        setIsLoading(false);
      })
      .catch(function (error) {
        if (error.code === 'ERR_CANCELED') return;
        console.error('Error fetching products:', error);
        setIsLoading(false);
      });
  }, [authUser?.token, categories, value, gender, visionUserMode]);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, categories, gender]);

  const handlerCategories = useCallback((value) => {
    setCategories(value);
  }, []);

  const handlerSearch = useCallback((value) => {
    setSearch(value);
  }, []);
  
  const handleGenderFilter = useCallback((selectedGender) => {
    setGender(selectedGender);
  }, []);

  return (
    <div className={`min-h-dvh flex flex-col ${darkMode ? 'dark bg-gray-900 text-white' : 'bg-white text-black'}`}>
      <Header />
      <FindBar
        onSearch={handlerSearch}
        onGenderChange={handleGenderFilter}
        onCategoriesChange={handlerCategories}
        initialGender={gender}
        hideGender={true}
        searchValue={search}
      />
      
      {/* Scrollable content */}
      <div className="flex-grow overflow-auto dark:bg-gray-900">
        <Container className="pt-16">
          {/* Contenedor de las tarjetas con flex y centrado en móvil */}
          <div className='flex flex-col items-center justify-center md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 px-4 -mt-16'>
            {models.map((model, index) => {
              const cardContent = (
                <div className="w-full max-w-[300px] mx-auto"> {/* Limita el ancho de las tarjetas en móvil y centra */}
                  <CardModel model={model} index={index} />
                </div>
              );

              if (models.length === index + 1) {
                return (
                  <Link
                    ref={lastModelRef}
                    to={`/profileModal/${model.user_id}`}
                    key={model.id}
                    className="w-full flex justify-center" // Centra la tarjeta en móvil
                  >
                    {cardContent}
                  </Link>
                );
              } else {
                return (
                  <Link
                    to={`/profileModal/${model.user_id}`}
                    key={model.id}
                    className="w-full flex justify-center" // Centra la tarjeta en móvil
                  >
                    {cardContent}
                  </Link>
                );
              }
            })}
          </div>
          {isLoading && <Loading />}
        </Container>
      </div>
      <ScrollToTopButton />
      <Modal ref={modalRef} />
    </div>
  );
});

export default Landing;
